import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReceiptAdvance } from '../../../interfaces/receipt.model';

export interface AdvanceExpensesDialogData {
  acconti: ReceiptAdvance[];
  importoMassimo: number;
}

@Component({
  providers: [provideTranslocoScope('receipts')],
  selector: 'rev-receipts-advance-expenses-dialog-component',
  imports: [TranslocoPipe, FormsModule, MatButtonModule, MatDialogModule, MatFormFieldModule, MatInputModule],
  templateUrl: './receipts-advance-expenses-dialog-component.html',
  styleUrl: './receipts-advance-expenses-dialog-component.scss',
})
export class ReceiptsAdvanceExpensesDialogComponent {
  readonly acconti: ReceiptAdvance[];
  readonly importoMassimo: number;

  constructor(@Inject(MAT_DIALOG_DATA) data: AdvanceExpensesDialogData) {
    this.acconti = data.acconti.map(acconto => ({ ...acconto }));
    this.importoMassimo = Math.max(0, Number(data.importoMassimo) || 0);
  }

  get totale(): number {
    return this.acconti.reduce(
      (sum, acconto) => sum + Math.max(0, Number(acconto.importo) || 0),
      0,
    );
  }

  get residuoDisponibile(): number {
    return Math.max(0, this.importoMassimo - this.totale);
  }

  get importoNonValido(): boolean {
    return this.totale > this.importoMassimo;
  }

  updateAmount(acconto: ReceiptAdvance, event: Event): void {
    const input = event.target as HTMLInputElement;
    const normalizedValue = input.value.replace('.', ',');
    const [integerPart, ...decimalParts] = normalizedValue.split(',');
    const sanitizedValue = integerPart.replace(/\D/g, '')
      + (decimalParts.length ? `,${decimalParts.join('').replace(/\D/g, '')}` : '');
    const value = Number(sanitizedValue.replace(',', '.'));

    input.value = sanitizedValue;
    acconto.importo = Number.isFinite(value) && value >= 0 ? value : 0;
  }

  prepareAmountInput(acconto: ReceiptAdvance, event: FocusEvent): void {
    if (acconto.importo !== 0) {
      return;
    }

    (event.target as HTMLInputElement).value = '';
  }

  commitAmount(acconto: ReceiptAdvance, event: FocusEvent): void {
    (event.target as HTMLInputElement).value = this.formatInputAmount(acconto.importo);
  }

  clearAmount(acconto: ReceiptAdvance, input: HTMLInputElement): void {
    acconto.importo = 0;
    input.value = this.formatInputAmount(0);
  }

  formatInputAmount(value: number): string {
    return (Number(value) || 0).toLocaleString('it-IT', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  formatAmount(value: number): string {
    return value.toLocaleString('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}
